import type { Ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import { computed, onMounted } from 'vue';

import { getStrCell } from '@/utils/file';
import useBookedJobsListFetch from '@/composables/data/useBookedJobsListFetch';
import useDraftsListFetch from '@/composables/data/useDraftsListFetch';
import { formatRelativeFromNow } from '@/helpers/date';
import * as routeNames from '@/constants/routeNames';

export const DRAFTS_PAGE_SIZE = 5;
export const BOOKED_JOBS_PAGE_SIZE = 5;

export interface DraftListItem {
  id: number;
  label: string;
  sublabel: string;
  meta: string;
  to: RouteLocationRaw;
}

export interface BookedJobListItem {
  id: number;
  label: string;
  sublabel: string;
  meta: string;
  to: RouteLocationRaw;
}

interface HomeJobBookingViewDataComposable {
  isFetchingDrafts: Ref<boolean>;
  draftsTotal: Ref<number | undefined>;
  draftsError: Ref<boolean>;
  draftItems: Ref<DraftListItem[]>;
  isFetchingBookedJobs: Ref<boolean>;
  bookedJobsTotal: Ref<number | undefined>;
  bookedJobsError: Ref<boolean>;
  bookedJobItems: Ref<BookedJobListItem[]>;
}

export default function useHomeJobBookingViewData(): HomeJobBookingViewDataComposable {
  const {
    isFetching: isFetchingDrafts,
    items: drafts,
    total: draftsTotal,
    isError: draftsError,
    fetchDrafts,
  } = useDraftsListFetch();

  const draftItems = computed<DraftListItem[]>(
    () =>
      drafts.value?.map((d) => ({
        id: d.id,
        label: d.name,
        sublabel: formatRelativeFromNow(d.modified),
        meta: d.created_by,
        to: { name: routeNames.DRAFT_JOB_BOOKING },
      })) ?? [],
  );

  const {
    isFetching: isFetchingBookedJobs,
    items: bookedJobs,
    total: bookedJobsTotal,
    isError: bookedJobsError,
    fetchBookedJobs,
  } = useBookedJobsListFetch();

  const bookedJobItems = computed<BookedJobListItem[]>(
    () =>
      bookedJobs.value?.map((job) => ({
        id: job.id,
        label: `Job #${job.rockhopper_id}`,
        sublabel: formatRelativeFromNow(job.created_at),
        meta: getStrCell(job.source_text),
        to: {
          name: routeNames.DETAILED_QUERY_JOB,
          query: {
            job_id: job.rockhopper_id,
            job_date: job.rockhopper_date,
          },
        },
      })) ?? [],
  );

  onMounted(() => {
    void fetchDrafts(1, DRAFTS_PAGE_SIZE).catch(() => undefined);
    void fetchBookedJobs(1, BOOKED_JOBS_PAGE_SIZE).catch(() => undefined);
  });

  return {
    isFetchingDrafts,
    draftsTotal,
    draftsError,
    draftItems,
    isFetchingBookedJobs,
    bookedJobsTotal,
    bookedJobsError,
    bookedJobItems,
  };
}
